// libraries
import { GoogleMap, GoogleMapProps, useJsApiLoader } from '@react-google-maps/api';
import { useEffect, useRef, useState } from 'react';

// 말레이시아 현장
const CENTER = {
  lat: 1.3502601602820203,
  lng: 104.1643852984741
};

const tmsConfig = {
  url: 'https://cs.carta.is/carta/workspace/1277/30814/universal/orthophoto/{z}/{x}/{y}.png?',
  minZoom: 1,
  maxZoom: 22,
  tileSize: 256
};

const TMS_BOUNDS = {
  north: 35.96502946075092,
  south: 35.96304281350489,
  east: 126.99268758790249,
  west: 126.98902855493733
};

// CENTER 기준으로 "고정 크기" bounds 생성 (테스트용)
const createBoundsFromCenter = (
  center: { lat: number; lng: number },
  widthMeters: number,
  heightMeters: number
) => {
  const R = 6378137; // Earth radius (m)
  const latRad = (center.lat * Math.PI) / 180;

  const dLat = (heightMeters / 2 / R) * (180 / Math.PI);
  const dLng = (widthMeters / 2 / (R * Math.cos(latRad))) * (180 / Math.PI);

  return {
    north: center.lat + dLat,
    south: center.lat - dLat,
    east: center.lng + dLng,
    west: center.lng - dLng
  };
};

const FLOOR_BOUNDS = createBoundsFromCenter(CENTER, 1000, 800);
const FLOOR_IMAGE_URL = '/assets/image/map/saf.png';

type MapProps = Omit<GoogleMapProps, 'center'> & { floorOn?: boolean };

export const Map = ({ floorOn = false, ...props }: MapProps) => {
  const mapRef = useRef<google.maps.Map | null>(null);

  const tmsOverlayRef = useRef<google.maps.ImageMapType | null>(null);
  const overlayIndexRef = useRef<number | null>(null);
  const [tmsOn, setTmsOn] = useState(false);

  const floorOverlayRef = useRef<google.maps.GroundOverlay | null>(null);

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-saf',
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_KEY
  });

  const createTmsOverlay = () =>
    new google.maps.ImageMapType({
      name: 'TMS',
      tileSize: new google.maps.Size(tmsConfig.tileSize, tmsConfig.tileSize),
      minZoom: tmsConfig.minZoom,
      maxZoom: tmsConfig.maxZoom,
      getTileUrl: (coord, zoom) => {
        const z = zoom;
        const x = coord.x;
        const y = coord.y;
        const tmsY = (1 << z) - y - 1;

        return tmsConfig.url
          .replace('{z}', String(z))
          .replace('{x}', String(x))
          .replace('{y}', String(tmsY));
      }
    });

  const attachTms = () => {
    const map = mapRef.current;
    if (!map) return;

    // 이미 붙어있으면 스킵
    if (tmsOverlayRef.current) return;

    const overlay = createTmsOverlay();
    const overlays = map.overlayMapTypes;

    overlays.push(overlay);
    tmsOverlayRef.current = overlay;
    overlayIndexRef.current = overlays.getLength() - 1;
  };

  const detachTms = () => {
    const map = mapRef.current;
    const overlay = tmsOverlayRef.current;
    if (!map || !overlay) return;

    const overlays = map.overlayMapTypes;

    // index를 알고 있으면 빠르게 제거, 아니면 탐색 제거
    const idx = overlayIndexRef.current;
    if (idx != null && overlays.getAt(idx) === overlay) {
      overlays.removeAt(idx);
    } else {
      for (let i = overlays.getLength() - 1; i >= 0; i--) {
        if (overlays.getAt(i) === overlay) {
          overlays.removeAt(i);
          break;
        }
      }
    }

    tmsOverlayRef.current = null;
    overlayIndexRef.current = null;
  };

  const attachFloorOverlay = () => {
    const map = mapRef.current;
    if (!map) return;
    if (floorOverlayRef.current) return;

    const bounds = new google.maps.LatLngBounds(
      { lat: FLOOR_BOUNDS.south, lng: FLOOR_BOUNDS.west },
      { lat: FLOOR_BOUNDS.north, lng: FLOOR_BOUNDS.east }
    );

    const overlay = new google.maps.GroundOverlay(FLOOR_IMAGE_URL, bounds, {
      clickable: false
      // opacity: 0.95,
    });

    overlay.setMap(map);
    floorOverlayRef.current = overlay;

    map.fitBounds(bounds, 40);
  };

  const detachFloorOverlay = () => {
    floorOverlayRef.current?.setMap(null);
    floorOverlayRef.current = null;
  };

  useEffect(() => {
    if (!isLoaded) return;
    if (!mapRef.current) return;

    if (floorOn) {
      attachFloorOverlay();
    } else {
      detachFloorOverlay();
    }
  }, [floorOn, isLoaded]);

  const goHome = () => {
    const map = mapRef.current;
    if (!map) return;

    map.setCenter(CENTER);
    map.setZoom(16);
  };

  if (!isLoaded) {
    return <div></div>;
  }

  return (
    <>
      <GoogleMap
        onLoad={(map) => {
          mapRef.current = map;
          if (floorOn) attachFloorOverlay();
        }}
        center={CENTER}
        zoom={16}
        mapContainerStyle={{
          width: '100%',
          height: '100%'
        }}
        options={{
          mapTypeControl: false,
          mapTypeId: floorOn ? google.maps.MapTypeId.ROADMAP : google.maps.MapTypeId.HYBRID,

          styles: floorOn
            ? [
                { elementType: 'geometry', stylers: [{ visibility: 'off' }] },
                { elementType: 'labels', stylers: [{ visibility: 'off' }] },
                { featureType: 'poi', stylers: [{ visibility: 'off' }] },
                { featureType: 'road', stylers: [{ visibility: 'off' }] },
                { featureType: 'transit', stylers: [{ visibility: 'off' }] },
                { featureType: 'water', stylers: [{ visibility: 'off' }] }
              ]
            : undefined
        }}
        {...props}
      />
      {/* ON/OFF 버튼(간단 오버레이 UI) */}
      {!floorOn && (
        <div style={{ position: 'absolute', top: 70, right: 20, zIndex: 10 }}>
          <button
            type="button"
            onClick={() => {
              const map = mapRef.current;
              if (!map) return;

              const next = !tmsOn;
              setTmsOn(next);

              if (next) {
                // ON → TMS 등록 + TMS 영역으로 이동
                attachTms();

                const bounds = new google.maps.LatLngBounds(
                  { lat: TMS_BOUNDS.south, lng: TMS_BOUNDS.west },
                  { lat: TMS_BOUNDS.north, lng: TMS_BOUNDS.east }
                );

                map.fitBounds(bounds, 40); // padding px
              } else {
                // OFF → TMS 제거 + 말레이시아 현장으로 복귀
                detachTms();
              }
            }}
            style={{
              padding: '8px 10px',
              borderRadius: 8,
              border: '1px solid rgba(255,255,255,0.35)',
              background: 'rgba(0,0,0,0.65)',
              color: '#fff',
              cursor: 'pointer'
            }}
          >
            Orthophoto: {tmsOn ? 'ON' : 'OFF'}
          </button>
        </div>
      )}
      {/* 하단 Home 버튼 */}
      <div
        style={{
          position: 'absolute',
          top: 110,
          right: 20,
          zIndex: 10
        }}
      >
        <button
          type="button"
          onClick={goHome}
          style={{
            padding: '10px 14px',
            borderRadius: 999,
            border: '1px solid rgba(255,255,255,0.35)',
            background: 'rgba(0,0,0,0.7)',
            color: '#fff',
            cursor: 'pointer',
            fontWeight: 600
          }}
        >
          Home
        </button>
      </div>
    </>
  );
};
